import { useInternalEditorState } from "@circle-react-shared/uikit/TipTap/internalEditorState";
import { useTipTapEditorContext } from "@circle-react-uikit/TipTap/TipTapEditorContext";
import { useBlockEditorContext } from "@circle-react-uikit/TipTapBlockEditor/BlockEditorContext";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { defer, flatten, groupBy, isArray } from "lodash";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "react-query";

import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import { useKeyboardHandler } from "../hooks/useKeyboardHandler";
import { EntityItem } from "./EntityItem";
import { EntityItem as EntityItemCompass } from "./EntityItemCompass";
import {
  findGroupIndexAndInsideGroupIndex,
  getEntityTypeLabel,
} from "./helpers";

interface Props {
  command: (item: any) => void;
  query: string;
  searchType?: string;
}

export const EntityList = forwardRef((props: Props, ref): any => {
  const { query, command, searchType = "entity_list" } = props;
  const tiptapEditorContext = useTipTapEditorContext();
  const tiptapBlockEditorContext = useBlockEditorContext();
  const { isCircleV4 } = useCircleV4();
  const { addToLocalSgidToObjectMap } =
    tiptapEditorContext || tiptapBlockEditorContext;
  const { setPopupsVisible } = useInternalEditorState();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const fetchEntityItems = () =>
    reactQueryGet(
      internalApi.searchV2.advanced.index({
        params: {
          query,
          per_page: 10,
          page: 1,
          type: searchType,
        },
      }),
    );

  const { data: items = [], isInitialLoading } = useQuery({
    queryKey: ["entities", searchType, query],
    queryFn: fetchEntityItems,

    select: ({ records }: any) =>
      (records || []).map((record: any) => ({
        ...record,
        label: record.name,
      })),

    enabled: true,
    retry: 1,
    placeholderData: keepPreviousData,
  });
  const hasItems = isArray(items) && items.length > 0;

  const selectItem = (entity: any) => {
    if (entity) {
      addToLocalSgidToObjectMap({
        sgid: entity.sgid,
        object: entity,
      });
      command(entity);
    }
  };

  useEffect(() => {
    if (isInitialLoading || !hasItems) {
      setPopupsVisible(false);
    } else {
      setPopupsVisible(true);
    }

    return () => setPopupsVisible(false);
  }, [hasItems, isInitialLoading, setPopupsVisible]);

  const entityGroups = groupBy(items, "type");
  const entityGroupsValues = Object.values(entityGroups);
  const groupSizes = entityGroupsValues.map(group => group.length);
  const flattenedData = flatten(entityGroupsValues);

  const checkIfIndexMatches = useCallback(
    (groupIndex: number, itemIndex: number) => {
      const {
        groupIndex: selectedGroupIndex,
        insideGroupIndex: selectedItemIndex,
      } = findGroupIndexAndInsideGroupIndex(selectedIndex, groupSizes);
      return (
        selectedGroupIndex === groupIndex && selectedItemIndex === itemIndex
      );
    },
    [selectedIndex, groupSizes],
  );

  useKeyboardHandler({
    selectedIndex,
    setSelectedIndex,
    ref,
    items: flattenedData,
    selectItem: (index: any) => {
      const item = flattenedData[index];
      selectItem(item);
    },
  });

  if (!hasItems) {
    return (
      <div className="h-[50vh] min-w-[250px] max-w-[320px] bg-transparent" />
    );
  }

  const EntityItemComponent = isCircleV4 ? EntityItemCompass : EntityItem;

  return (
    <div
      className={classNames(
        "bg-primary border-primary relative max-h-[550px] min-w-[250px] max-w-[320px] overflow-auto rounded-md border pt-4",
        isCircleV4 ? "p-1 space-y-2 shadow-2xs" : "space-y-6 text-sm shadow-md",
      )}
    >
      {entityGroupsValues.map((group, groupIndex) => {
        const type = group[0].type;
        return (
          <div key={type} className="flex flex-col">
            <div className={classNames(isCircleV4 ? "px-2 py-1" : "px-6 pb-2")}>
              <Typography.LabelSm color="text-light" weight="medium">
                {getEntityTypeLabel(type)}
              </Typography.LabelSm>
            </div>
            {group.map((entity, itemIndex) => (
              <EntityItemComponent
                key={entity.id}
                entity={entity}
                handleClick={() => {
                  selectItem(entity);
                  defer(() => setPopupsVisible(false));
                }}
                isSelected={checkIfIndexMatches(groupIndex, itemIndex)}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
});

EntityList.displayName = "EntityList";
